import React from 'react';
import { motion } from 'framer-motion';

const PortfolioSection: React.FC = () => {
  const projects = [
    {
      title: 'Ibay App',
      category: 'Mobile Marketplace',
      description: 'First online marketplace in Iba, Zambales connecting local stores with customers. A comprehensive platform that bridges the gap between local businesses and customers.',
      image: '/assets/iBayApp.png',
      features: ['Real-time Order Tracking', 'Secure Payment System', 'Multi-vendor Support'],
      status: 'Live',
      year: '2022',
      hasWhiteBackground: true
    },
    {
      title: 'LGU Services Portal',
      category: 'Government Platform',
      description: 'Digital platform for automated government services and permit applications. Streamlining bureaucratic processes for citizens and government offices.',
      image: '/assets/LGU-Automation.png',
      features: ['Online Business Permit', 'Online Community Tax', 'Online Barangay Clearance', 'Online Occupational Permit', 'Online Building Permit'],
      status: 'Live',
      year: '2025',
      link: 'https://ibaservicesportal.com/'
    },
    {
      title: 'RPT-Abra',
      category: 'Real Property Tax System',
      description: 'Real Property Tax (RPT) System, a comprehensive platform designed to streamline property tax declaration, assessment, and records management. The system enhances transparency and efficiency for both government agencies and taxpayers, offering online and on-premise access for various stakeholders involved in the property tax process.',
      image: '/assets/RPT-Abra.png',
      features: ['Database Management', 'User-Friendly Interface', 'Increased Efficiency', 'Improved Accuracy', 'Greater Transparency'],
      status: 'Live',
      year: '2025'
    },
    {
      title: 'EMR System',
      category: 'Healthcare Management',
      description: 'Electronic Medical Records system for healthcare facilities. Digitizing patient records and streamlining medical workflows for better healthcare delivery.',
      image: '/assets/IbayTech-Logo.png',
      features: ['Patient Profile & Records', 'Admin Dashboard', 'Medical History', 'Laboratory Management', 'Pharmacy & Supplies', 'Emergency Services', 'Consultation Scheduling', 'Reporting & Analytics'],
      status: 'Under Development',
      year: '2025'
    }
  ];

  return (
    <section className="section-padding bg-black">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-white mb-4 lg:mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our{' '}
            <span className="bg-gradient-to-r from-blue-400 via-red-400 to-white bg-clip-text text-transparent">
              Portfolio
            </span>
          </motion.h2>
          
          <motion.p
            className="text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Who is Ibayteach - Our Innovative Projects that make a difference in Iba, Zambales and beyond.
          </motion.p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
          {projects && projects.map((project, index) => (
            <motion.div
              key={index}
              className="group relative glass-card rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              {/* Project Image */}
              <div className={`relative h-40 sm:h-48 lg:h-56 overflow-hidden ${
                project.hasWhiteBackground 
                  ? 'bg-white' 
                  : 'bg-gradient-to-br from-gray-800 to-gray-900'
              }`}>
                <div className="w-full h-full flex items-center justify-center p-3 lg:p-4">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className={`object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 ${
                      project.title === 'LGU Services Portal' 
                        ? 'w-full h-full scale-125' 
                        : 'max-w-full max-h-full'
                    }`}
                  />
                </div>
              </div>

              {/* Project Info */}
              <div className="p-4 lg:p-6">
                <div className="mb-3 lg:mb-4">
                  <div className="flex items-center justify-between mb-1 lg:mb-2">
                    <h3 className="text-lg lg:text-xl font-bold text-white group-hover:text-gray-200 transition-colors">
                      {project.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      {/* Status Badge */}
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        project.status === 'Live' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : project.status === 'Active'
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}>
                        {project.status}
                      </span>
                      {/* Year Badge */}
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-white/10 text-white border border-white/20">
                        {project.year}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider">
                    {project.category}
                  </p>
                </div>

                <p className="text-gray-300 text-sm lg:text-base leading-relaxed mb-4 lg:mb-6">
                  {project.description}
                </p>

                {/* Key Features */}
                <div className="mb-4 lg:mb-6">
                  <h4 className="text-white text-sm lg:text-base font-semibold mb-2 lg:mb-3">Key Features:</h4>
                  <div className="flex flex-wrap gap-1 lg:gap-2">
                    {project.features && project.features.map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className="px-2 py-1 text-xs bg-white/10 text-gray-300 rounded border border-white/20"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* View Project Button - Commented out temporarily */}
                {/*
                {project.link ? (
                  <motion.a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-2 lg:py-3 bg-transparent border border-white/30 text-white rounded-lg hover:bg-white hover:text-black transition-all duration-300 font-medium text-center text-sm lg:text-base"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Visit Live Site
                    <svg className="inline-block ml-1 lg:ml-2 w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </motion.a>
                ) : (
                  <motion.button
                    className="w-full py-2 lg:py-3 bg-transparent border border-white/30 text-white rounded-lg hover:bg-white hover:text-black transition-all duration-300 font-medium text-sm lg:text-base"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    View Project Details
                  </motion.button>
                )}
                */}
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
